import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getPreSignedUrlToUpload } from '../../../../../../../redux/Spoolsbase/saga';
import { Notification } from '../../../../../../../components/ui/custom-notification';
import { fetchSpoolNcrList } from '../../../../../../../redux/Spoolsbase/actions';
import { filterNcrRowsByWeldId, type NcrRow } from '../../spoolNcrApi';
import { getRepairStripInsertionSummary } from '../../integratedRepairStrip';
import { WorkflowNcrSummaryTable } from '../weld/WorkflowNcrSummaryTable';
import { Button } from '../../../../../../../components/ui/button';
import { AntdDate } from '../../../../../../../components/ui/antd-date';
import { Label } from '../../../../../../../components/ui/label';
import { FileUpload } from '../../../../../../../components/ui/FileUpload';
import type { WeldJoint } from '../../SpoolsProduction';
import { Signature } from '../../Signature';
import type { SignaturePanelProps } from '../weld/signatureTypes';
import {
  FieldGroup,
  FormGrid,
  TriStateCheckRow,
  type TriStateCheck,
} from '../weld/spoolDetailFormUi';
import {
  buildQcSignaturePayload,
  fileListToMeta,
  mergeSpoolStepDocumentsByS3Key,
  normalizeSpoolStepDocumentsForSave,
  parseDocumentsFromStepPayload,
  triStateToApi,
  type SpoolStepPanelContext,
} from '../weld/spoolStepPayloadHelpers';
import { SpoolStepSavedDocumentsList } from '../weld/SpoolStepSavedDocumentsList';
import {
  getVtPanelPayloadForPhase,
  getVtPanelRowForPhase,
  resolveVtStepPanelRowId,
  weldPanelsRevision,
} from '../weld/weldStepPanelHydration';
import { Card } from '../../../../../../../components/ui/card';
import { CardContent } from '../../../../../../../components/ui/card';
import AddNewItemDialog from '../../../../../../../components/ui/AddNewItemDialog';
import { AddMaterialCategory, getListMaterialCategory } from '../../../../../../../redux/Project/materialSaga';
import type { DocumentTag } from '@/features/project/types';
import type { SpoolModalWorkflowStep } from '@/features/project/components/dashboard/spooltracker/repairWorkflowHelpers';

export type VTSubmissionMode = 'draft' | 'pass_vt';

export type VTCriterionKey =
  | 'capProfileAcceptable'
  | 'noUndercut'
  | 'noSpatterArcStrikes'
  | 'welderIdStampVerified'
  | 'isDoneByITSDept';

/** VT attachments after S3 upload via presign (`getPresignedUrlToUploadSpoolsbaseDoc`). */
export interface VtUploadedDocumentPayload {
  fileName: string;
  s3Key: string;
  uploadedBy: string;
  /** Registry category for spool documents (matches presign `type` / UI). */
  documentCategory?: string;
  /** Tag ids (strings) per file, aligned with equipment document tags. */
  tags?: string[];
  /** Present when updating an existing spool document row. */
  spoolDocId?: number;
}

export interface VTSavePayload {
  mode: VTSubmissionMode;
  projectId?: number | string | null;
  pipeId?: number | string | null;
  spoolId?: number | null;
  spoolName?: string | null;
  weldId?: string | null;
  weldNumber?: string | null;
  inspector: string;
  inspectionDate: string;
  /** Per-criterion outcome (aligned with Accept panel checks). */
  criteria: Record<VTCriterionKey, 'pass' | 'fail' | 'pending'>;
  notes: string;
  attachmentsMeta: ReturnType<typeof fileListToMeta>;
  signature: ReturnType<typeof buildQcSignaturePayload>;
  /** Targets an existing spool weld-step row (same as backend `stepPanelRowId`). */
  stepPanelRowId?: number;
  vtPhase?: 'standard' | 'retest';
  repairNo?: number | null;
  /** Optional: API field aligned with FAT / spool doc saves (`draft` | `submit`). */
  submissionMode?: 'draft' | 'submit';
  /** New uploads from this session (after presigned PUT to S3). */
  documents?: VtUploadedDocumentPayload[];
}

const VT_CRITERIA_ROWS: {
  key: VTCriterionKey;
  labels: { neutral: string; pass: string; fail: string };
}[] = [
  {
    key: 'capProfileAcceptable',
    labels: {
      neutral: 'Weld cap profile acceptable — pending',
      pass: 'Weld cap profile acceptable — PASSED',
      fail: 'Weld cap profile acceptable — FAILED',
    },
  },
  {
    key: 'noUndercut',
    labels: {
      neutral: 'No undercut exceeding acceptance criteria — pending',
      pass: 'No undercut exceeding acceptance criteria — PASSED',
      fail: 'No undercut exceeding acceptance criteria — FAILED',
    },
  },
  {
    key: 'noSpatterArcStrikes',
    labels: {
      neutral: 'No spatter, arc strikes, or surface cracks — pending',
      pass: 'No spatter, arc strikes, or surface cracks — PASSED',
      fail: 'No spatter, arc strikes, or surface cracks — FAILED',
    },
  },
  {
    key: 'welderIdStampVerified',
    labels: {
      neutral: 'Welder ID stamp verified — pending',
      pass: 'Welder ID stamp verified — PASSED',
      fail: 'Welder ID stamp verified — FAILED',
    },
  },
  {
    key: 'isDoneByITSDept',
    labels: {
      neutral: 'Report done by ITS — pending',
      pass: 'Report done by ITS — PASSED',
      fail: 'Report done by ITS — FAILED',
    },
  },
];

const NEUTRAL_VT_CRITERIA: Record<VTCriterionKey, TriStateCheck> = {
  capProfileAcceptable: 'neutral',
  noUndercut: 'neutral',
  noSpatterArcStrikes: 'neutral',
  welderIdStampVerified: 'neutral',
  isDoneByITSDept: 'neutral',
};

function criteriaFromPayload(c: Record<string, unknown>): Record<VTCriterionKey, TriStateCheck> {
  const next = { ...NEUTRAL_VT_CRITERIA };
  const keys = Object.keys(NEUTRAL_VT_CRITERIA) as VTCriterionKey[];
  for (const k of keys) {
    const v = c[k];
    if (v === true || v === 'pass') next[k] = 'pass';
    else if (v === 'fail') next[k] = 'fail';
    else if (typeof v === 'string') {
      const s = v.toLowerCase();
      if (s === 'pass') next[k] = 'pass';
      else if (s === 'fail') next[k] = 'fail';
      else next[k] = 'neutral';
    } else next[k] = 'neutral';
  }
  return next;
}

export interface VTPanelProps {
  weld: WeldJoint | null;
  sig: SignaturePanelProps;
  context?: SpoolStepPanelContext;
  /** When the workflow strip opens a second VT pill after repair weld (integrated repair strip). */
  vtPhase?: 'standard' | 'retest';
  /** Repair cycle from integrated strip / API — stored on payload for workflow transitions. */
  repairNo?: number | null;
  onVtSave?: (payload: VTSavePayload) => void | Promise<void>;
  onSubmittedNcrClick?: (row: NcrRow) => void;
  /** Jump to another workflow tab (e.g. Weld) to record a repair before VT retest. */
  onNavigateToStep?: (step: SpoolModalWorkflowStep) => void;
  /** Opens Raise NCR for VT (does not submit step save). */
  onOpenRaiseNcr?: () => void;
  /** Starts integrated repair strip from a VT NCR row (same workflow table). */
  onAddRepairWeld?: (row: NcrRow) => void;
}

export function VTPanel({
  weld,
  sig,
  context,
  vtPhase = 'standard',
  repairNo = null,
  onVtSave,
  onSubmittedNcrClick,
  onNavigateToStep,
  onOpenRaiseNcr,
  onAddRepairWeld,
}: VTPanelProps) {
  const dispatch = useDispatch();
  const ncrListAll = useSelector((s: { spoolsbase?: { ncrList?: NcrRow[] } }) => s.spoolsbase?.ncrList ?? []);
  const ncrList = useMemo(
    () =>
      filterNcrRowsByWeldId(ncrListAll, weld?.id ?? context?.weldId ?? null, { matchStepType: 'vt' }),
    [ncrListAll, weld?.id, context?.weldId]
  );
  const ncrListLoading = useSelector(
    (s: { spoolsbase?: { ncrListLoading?: boolean } }) => s.spoolsbase?.ncrListLoading ?? false
  );
  const [inspector, setInspector] = useState('J. Garcia');
  const [inspectionDate, setInspectionDate] = useState('2026-04-06');
  const [vtCriteria, setVtCriteria] = useState<Record<VTCriterionKey, TriStateCheck>>(() => ({
    ...NEUTRAL_VT_CRITERIA,
  }));
  const [notes, setNotes] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [documentTags, setDocumentTags] = useState<DocumentTag[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[][]>([]);
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [activeSubmit, setActiveSubmit] = useState<VTSubmissionMode | null>(null);
  const activeSubmitRef = useRef<VTSubmissionMode | null>(null);
  const vtRemoteSaving = useSelector(
    (s: { spoolsbase?: { isSaving?: boolean; savingStep?: string | null } }) =>
      Boolean(s.spoolsbase?.isSaving && s.spoolsbase?.savingStep === 'vt')
  );
  const authUser = useSelector((s: { auth?: { user?: { email?: string } } }) => s.auth?.user);

  const resolvedVtStepPanelRowId = useMemo(
    () => resolveVtStepPanelRowId(weld ?? null, vtPhase),
    [weld, vtPhase, weldPanelsRevision(weld ?? null)]
  );

  const vtPanelRow = useMemo(
    () => getVtPanelRowForPhase(weld ?? null, vtPhase),
    [weld, vtPhase, weldPanelsRevision(weld ?? null)]
  );

  const persistedDocuments = useMemo(() => {
    const pl = getVtPanelPayloadForPhase(weld ?? null, vtPhase);
    return parseDocumentsFromStepPayload(pl ?? undefined);
  }, [weld, vtPhase, weldPanelsRevision(weld ?? null)]);

  const downloadContext = useMemo(() => {
    const projectId = Number(context?.projectId);
    const pipeId = Number(context?.pipeId);
    const projectTypeId = Number(context?.projectTypeId);
    const spoolId = Number(context?.spoolId);
    if (![projectId, pipeId, projectTypeId, spoolId].every(Number.isFinite)) return null;
    return { projectId, pipeId, projectTypeId, spoolId };
  }, [context?.projectId, context?.pipeId, context?.projectTypeId, context?.spoolId]);

  useEffect(() => {
    const pid = context?.projectId;
    const poid = context?.pipeId;
    if (pid == null || pid === '' || poid == null || poid === '') return;
    void dispatch(
      fetchSpoolNcrList({ projectId: pid, pipeId: poid, spoolId: context?.spoolId ?? undefined })
    );
  }, [context?.projectId, context?.pipeId, context?.spoolId, dispatch]);

  useEffect(() => {
    let cancelled = false;
    const loadTags = async () => {
      try {
        const tags = await getListMaterialCategory();
        if (!cancelled && tags && Array.isArray(tags)) {
          setDocumentTags(tags);
        }
      } catch {
        /* ignore */
      }
    };
    void loadTags();
    return () => {
      cancelled = true;
    };
  }, []);

  const tagOptions = useMemo(
    () =>
      documentTags
        .filter((tag) => tag.tags_type === 'EQUIPMENT_AND_CONSUMABLE_DOCUMENT_TAGS')
        .map((tag) => ({
          value: tag.tags_id.toString(),
          label: tag.tags_name,
        })),
    [documentTags]
  );

  useEffect(() => {
    if (!weld) {
      setVtCriteria({ ...NEUTRAL_VT_CRITERIA });
      setNotes('');
      setFiles([]);
      setSelectedTags([]);
      return;
    }
    const pl = getVtPanelPayloadForPhase(weld, vtPhase);
    if (!pl || Object.keys(pl).length === 0) {
      setVtCriteria({ ...NEUTRAL_VT_CRITERIA });
      setNotes('');
      return;
    }
    if (pl.inspector != null) setInspector(String(pl.inspector));
    if (pl.inspectionDate != null) setInspectionDate(String(pl.inspectionDate));
    if (pl.criteria != null && typeof pl.criteria === 'object' && pl.criteria !== null) {
      setVtCriteria(criteriaFromPayload(pl.criteria as Record<string, unknown>));
    }
    if (pl.notes != null) setNotes(String(pl.notes));
  }, [weld, weld?.id, vtPhase, weldPanelsRevision(weld)]);

  const handleMaterialFileSelect = (selectedFiles: FileList | null) => {
    if (selectedFiles && selectedFiles.length > 0) {
      const fileArray = Array.from(selectedFiles);
      setFiles((prev) => [...prev, ...fileArray]);
      setSelectedTags((prev) => [...prev, ...fileArray.map(() => [] as string[])]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setSelectedTags((prev) => prev.filter((_, i) => i !== index));
  };

  const handleTagsChange = (fileIndex: number, tags: string[]) => {
    setSelectedTags((prev) => {
      const next = [...prev];
      next[fileIndex] = tags;
      return next;
    });
  };

  const handleAddTag = async (tagName: string) => {
    const v = tagName.trim();
    if (!v) return;
    const pid = context?.projectId;
    if (pid == null || pid === '') {
      Notification.error('VT', 'Project context required to add tags.');
      return;
    }
    if (
      documentTags.some(
        (tag) =>
          tag.tags_name === v && tag.tags_type === 'EQUIPMENT_AND_CONSUMABLE_DOCUMENT_TAGS'
      )
    ) {
      Notification.warning('VT', `${v} tag already exists`);
      return;
    }
    const res = await AddMaterialCategory({
      tags_name: v,
      project_id: Number(pid),
      tags_type: 'EQUIPMENT_AND_CONSUMABLE_DOCUMENT_TAGS',
      created_by: authUser?.email || '',
    });
    if (res) {
      setDocumentTags((prev) => [...prev, res]);
      setIsAddingTag(false);
    }
  };

  const cycleVtCriterion = (key: VTCriterionKey) => {
    setVtCriteria((prev) => {
      const cur = prev[key];
      const next: TriStateCheck =
        cur === 'neutral' ? 'pass' : cur === 'pass' ? 'fail' : 'neutral';
      return { ...prev, [key]: next };
    });
  };

  const buildPayload = useCallback(
    (mode: VTSubmissionMode): VTSavePayload => {
      const criteria = (Object.keys(NEUTRAL_VT_CRITERIA) as VTCriterionKey[]).reduce(
        (acc, k) => {
          acc[k] = triStateToApi(vtCriteria[k]);
          return acc;
        },
        {} as Record<VTCriterionKey, 'pass' | 'fail' | 'pending'>
      );
      const stepPanelRowIdOut =
        resolvedVtStepPanelRowId != null && resolvedVtStepPanelRowId > 0
          ? resolvedVtStepPanelRowId
          : undefined;
      const submissionMode: 'draft' | 'submit' = mode === 'draft' ? 'draft' : 'submit';
      return {
        mode,
        submissionMode,
        projectId: context?.projectId ?? null,
        pipeId: context?.pipeId ?? null,
        spoolId: context?.spoolId ?? null,
        spoolName: context?.spoolName ?? null,
        weldId: context?.weldId ?? weld?.id ?? null,
        weldNumber: context?.weldNumber ?? weld?.number ?? null,
        inspector,
        inspectionDate,
        criteria,
        notes: notes.trim(),
        attachmentsMeta: fileListToMeta(files),
        signature: buildQcSignaturePayload(sig),
        ...(stepPanelRowIdOut != null ? { stepPanelRowId: stepPanelRowIdOut } : {}),
        ...(vtPhase === 'retest' ? { vtPhase: 'retest' as const } : {}),
        ...(repairNo != null && !Number.isNaN(Number(repairNo)) ? { repairNo: Number(repairNo) } : {}),
      };
    },
    [
      context,
      weld?.id,
      weld?.number,
      inspector,
      inspectionDate,
      vtCriteria,
      notes,
      files,
      sig,
      resolvedVtStepPanelRowId,
      vtPhase,
      repairNo,
    ]
  );

  const submit = async (mode: VTSubmissionMode) => {
    if (!onVtSave) return;

    let newUploadDocuments: VtUploadedDocumentPayload[] | undefined;

    if (files.length > 0) {
      const projectId = Number(context?.projectId);
      const pipeId = Number(context?.pipeId);
      const projectTypeId = Number(context?.projectTypeId);
      const spoolId = Number(context?.spoolId);
      if (
        !Number.isFinite(projectId) ||
        !Number.isFinite(pipeId) ||
        !Number.isFinite(projectTypeId) ||
        !Number.isFinite(spoolId)
      ) {
        Notification.error(
          'VT',
          'Missing project, pipe, spool, or project type — cannot upload documents. Open this weld from a spool context.'
        );
        return;
      }

      const uploadPayload = {
        projectId,
        pipeId,
        spoolId,
        projectTypeId,
        type: 'Visual Inspection',
      };

      const s3Response = await getPreSignedUrlToUpload(files, uploadPayload, 1);

      if (!s3Response || s3Response.length === 0) {
        Notification.error('VT', 'Document upload failed. Please try again.');
        return;
      }

      const uploadedBy = authUser?.email?.trim() || '';
      newUploadDocuments = s3Response.map((f: { fileName: string; s3Key: string }, index: number) => ({
        fileName: f.fileName,
        s3Key: f.s3Key,
        uploadedBy,
        documentCategory: 'Visual Inspection',
        tags: selectedTags[index] ?? [],
      }));
    }

    const mergedDocuments =
      newUploadDocuments != null && newUploadDocuments.length > 0
        ? mergeSpoolStepDocumentsByS3Key(persistedDocuments, newUploadDocuments)
        : persistedDocuments;

    const payload: VTSavePayload = {
      ...buildPayload(mode),
      ...(mergedDocuments.length > 0
        ? { documents: normalizeSpoolStepDocumentsForSave(mergedDocuments) }
        : {}),
    };

    activeSubmitRef.current = mode;
    setActiveSubmit(mode);
    setFiles([]);
    setSelectedTags([]);
    await Promise.resolve(onVtSave(payload));
  };

  useEffect(() => {
    if (!vtRemoteSaving) {
      activeSubmitRef.current = null;
      setActiveSubmit(null);
    }
  }, [vtRemoteSaving]);

  const hasExistingVt = useMemo(() => {
    const pl = getVtPanelPayloadForPhase(weld ?? null, vtPhase);
    return !!pl && Object.keys(pl).length > 0;
  }, [weld, vtPhase, weldPanelsRevision(weld ?? null)]);
  const actionText = hasExistingVt ? 'Update VT — Release for NDT' : 'Save VT — Release for NDT';
  const passLoadingText = hasExistingVt ? 'Updating…' : 'Saving…';
  const kind = activeSubmit ?? activeSubmitRef.current;
  const savingDraft = vtRemoteSaving && kind === 'draft';
  const savingPassVt = vtRemoteSaving && kind === 'pass_vt';
  const draftDisabled = !weld || savingPassVt;
  const passDisabled = !weld || savingDraft;

  const showWorkflowNcrTable =
    vtPhase !== 'retest' &&
    repairNo == null &&
    ncrList.length > 0 &&
    onSubmittedNcrClick &&
    context?.projectId != null &&
    context?.projectId !== '' &&
    context?.pipeId != null &&
    context?.pipeId !== '';

  return (
    <div className="grid grid-cols-1">
      <Card className="flex max-h-[min(75vh,720px)] min-h-0 flex-col overflow-hidden border border-gray-200">
        <CardContent className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-y-contain p-4">
      {showWorkflowNcrTable ? (
        <WorkflowNcrSummaryTable
          ncrList={ncrList}
          loading={ncrListLoading}
          step="vt"
          stepTitle="VT"
          weldId={weld?.id ?? null}
          onRowClick={onSubmittedNcrClick}
          onAddRepairWeld={onAddRepairWeld}
          repairPlanHint={onAddRepairWeld ? getRepairStripInsertionSummary('vt') : undefined}
          repairActionLabel="Start VT repair"
        />
      ) : null}
      <FormGrid>
        <FieldGroup label="Inspection Date" required>
          <AntdDate className="h-9 text-sm" value={inspectionDate} onChange={setInspectionDate} />
        </FieldGroup>
      </FormGrid>
      <div className="space-y-2 mt-2">
        {VT_CRITERIA_ROWS.map(({ key, labels }) => (
          <TriStateCheckRow
            key={key}
            tag="VT"
            labels={labels}
            state={vtCriteria[key]}
            onCycle={() => cycleVtCriterion(key)}
          />
        ))}
      </div>
      <FieldGroup label="Notes / Observations">
        <textarea
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          rows={2}
          placeholder="Describe any observations..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </FieldGroup>
      <div className="space-y-2">
        <Label>Upload Documents</Label>
        <p className="text-sm text-gray-600">
          Upload the Visual Inspection Photos at 12 o&apos;clock and 6 o&apos;clock minimum 2 photos per weld.
          <br />
          <span className="text-xs text-gray-500">
            Allowed: PDF, Word (.doc, .docx), Excel (.xls, .xlsx), Text (.txt), Images (.jpg, .png, .gif)
            <br />
            Maximum file size: 100MB per file
          </span>
        </p>

        <SpoolStepSavedDocumentsList
          documents={persistedDocuments}
          rowUpdatedAt={vtPanelRow?.updatedAt ?? null}
          downloadContext={downloadContext}
        />

        <FileUpload
          onFileSelect={handleMaterialFileSelect}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
          multiple={true}
          maxFiles={100}
          buttonText="Upload Documents"
          variant="outline"
          size="lg"
          onRemoveFile={handleRemoveFile}
          selectedFiles={files}
          showFileList={files.length > 0}
          className="w-full"
          showTags={true}
          optionsTags={tagOptions}
          selectedTags={selectedTags}
          showAddButton={true}
          addButtonText="Add New Tag"
          onAddButtonClick={() => setIsAddingTag(true)}
          onTagsChange={handleTagsChange}
          maxHeight=""
        />

        <div className="text-xs text-gray-500">
          Current files: {files.length} file(s)
        </div>
      </div>

      <AddNewItemDialog
        open={isAddingTag}
        onOpenChange={setIsAddingTag}
        title="Add New Tag"
        label="Tag Name"
        placeholder="Enter tag name"
        onAdd={handleAddTag}
        buttonText="Add Tag"
        cancelText="Cancel"
      />

      <Signature
        customerSignature={null}
        qcSignature={sig.selectedQcId}
        customerRepresentativeList={[]}
        qcList={sig.qcList}
        handleQcSignatureChange={sig.onQcChange}
        signatureData={sig.qcSignatureData}
        onSignatureChange={sig.onQcSignatureChange}
        showCustomerRep={false}
        showQc={true}
        qcLabel="QC Inspector"
        canSign={true}
        canEdit={true}
        isEditMode={true}
        isAdminOrSuperAdmin={true}
      />
      <div className="flex justify-end gap-2 mt-2">
        <Button
          variant="outline"
          size="sm"
          type="button"
          className="border-red-300 text-red-600 hover:bg-red-50"
          disabled={!weld || savingDraft || savingPassVt}
          onClick={() => onOpenRaiseNcr?.()}
        >
          Fail — Raise NCR
        </Button>
        <Button
          variant="outline"
          size="sm"
          type="button"
          disabled={draftDisabled}
          loading={savingDraft}
          loadingText="Saving draft…"
          onClick={() => void submit('draft')}
        >
          Save Draft
        </Button>
        <Button
          type="button"
          size="sm"
          className="bg-hero-red text-white hover:bg-hero-red-60"
          disabled={passDisabled}
          loading={savingPassVt}
          loadingText={passLoadingText}
          onClick={() => void submit('pass_vt')}
        >
          {actionText}
        </Button>
      </div>
      </CardContent>
    </Card>
    </div>
  );
}
